import React, { useState } from 'react';
import {
  Typography, Box, Chip, Collapse,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
  useMediaQuery,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const STATUS_STYLE = {
  completed: { bg: 'rgba(74,222,128,0.12)', color: '#4ade80', label: 'Completed' },
  running:   { bg: 'rgba(251,146,60,0.15)', color: '#fb923c', label: 'Running' },
  failed:    { bg: 'rgba(248,113,113,0.15)', color: '#f87171', label: 'Failed' },
};

// Tooltip text for the Partial chip: which phases failed, per domain
const partialTooltip = (scan) => {
  const fallback = 'Some scan phases failed: results may be incomplete';
  try {
    const fails = JSON.parse(scan.phase_summary || '[]');
    if (!fails.length) return fallback;
    const items = fails.slice(0, 6).map(
      (f) => `${f.domain ? f.domain + ': ' : ''}Phase ${f.phase} ${f.name}`
    );
    const more = fails.length > 6 ? ` (+${fails.length - 6} more)` : '';
    return `Results may be incomplete. Failed: ${items.join('; ')}${more}`;
  } catch {
    return fallback;
  }
};

function ScanHistory({ history, selectedScanId, onScanSelect, onOpenHelp }) {
  const [expanded, setExpanded] = useState(true);
  const isMobile = useMediaQuery('(max-width:768px)');

  const handleRowClick = (scan) => {
    if (scan.status !== 'completed') return;
    onScanSelect(selectedScanId === scan.id ? null : scan.id);
  };

  const headSx = {
    fontWeight: 600, fontSize: '0.65rem', color: 'rgba(232,228,248,0.3)', py: 0.8,
    textTransform: 'uppercase', letterSpacing: 0.5, background: '#13101f', zIndex: 2,
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  };

  return (
    <Box sx={{ bgcolor: '#13101f', borderRadius: 2, border: '1px solid rgba(255,255,255,0.07)',
               overflow: 'hidden' }}>
      {/* Collapsible header */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1.2, cursor: 'pointer',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' },
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <HistoryIcon sx={{ fontSize: 16, color: 'rgba(232,228,248,0.35)' }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase',
                            letterSpacing: 0.8, color: 'rgba(232,228,248,0.35)' }}>
            Scan History
          </Typography>
          <Typography sx={{ fontSize: '0.63rem', color: 'rgba(232,228,248,0.2)' }}>
            click a completed scan to filter alerts
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {selectedScanId && (
            <Chip
              label={`Scan #${selectedScanId}`}
              size="small"
              onDelete={() => onScanSelect(null)}
              sx={{
                height: 20, fontSize: '0.65rem',
                bgcolor: 'rgba(124,92,191,0.15)', color: '#a78bfa',
                '& .MuiChip-deleteIcon': { color: '#a78bfa', fontSize: 14 },
              }}
            />
          )}
          {expanded
            ? <KeyboardArrowUpIcon sx={{ fontSize: 18, color: 'rgba(232,228,248,0.3)' }} />
            : <KeyboardArrowDownIcon sx={{ fontSize: 18, color: 'rgba(232,228,248,0.3)' }} />}
        </Box>
      </Box>

      <Collapse in={expanded}>
        {/* No vertical cap on mobile — let the list flow into the page scroll
            instead of trapping rows in a 220px touch-scroll box. */}
        <TableContainer sx={{ maxHeight: isMobile ? 'none' : 220 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {['Date', 'Source', 'Status', 'Duration', 'Detected'].map((h) => (
                  <TableCell key={h} sx={headSx}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {history.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3, borderBottom: 'none' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
                      No scans yet
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                history.map((scan) => {
                  const isSelected = selectedScanId === scan.id;
                  const isClickable = scan.status === 'completed';
                  const s = STATUS_STYLE[scan.status] || {
                    bg: 'rgba(255,255,255,0.07)', color: 'rgba(232,228,248,0.45)', label: scan.status,
                  };
                  const isScheduled = scan.trigger === 'scheduled';
                  const isNrd = scan.trigger === 'nrd';
                  const isFeed = scan.trigger === 'feed';

                  return (
                    <TableRow
                      key={scan.id}
                      onClick={() => handleRowClick(scan)}
                      sx={{
                        cursor: isClickable ? 'pointer' : 'default',
                        bgcolor: isSelected ? 'rgba(124,92,191,0.1) !important' : undefined,
                        '&:hover': isClickable ? { bgcolor: 'rgba(255,255,255,0.03) !important' } : {},
                      }}
                    >
                      <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap', py: 0.8 }}>
                        {new Date(scan.scan_date + 'Z').toLocaleDateString('en-US', { month: 'short', day: '2-digit' }) + ', ' + new Date(scan.scan_date + 'Z').toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
                      </TableCell>
                      <TableCell sx={{ py: 0.8 }}>
                        <Chip
                          label={isFeed ? 'Feed' : isNrd ? 'NRD' : isScheduled ? 'Auto' : 'Manual'}
                          size="small"
                          sx={{
                            bgcolor: isFeed ? 'rgba(251,191,36,0.14)'
                              : isNrd ? 'rgba(45,212,191,0.15)'
                              : isScheduled ? 'rgba(124,92,191,0.18)' : 'rgba(255,255,255,0.07)',
                            color: isFeed ? '#fbbf24'
                              : isNrd ? '#2dd4bf'
                              : isScheduled ? '#a78bfa' : 'rgba(232,228,248,0.4)',
                            fontSize: '0.62rem', fontWeight: 600, height: 18,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.8 }}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Chip label={s.label} size="small"
                            sx={{ bgcolor: s.bg, color: s.color, fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                          {Boolean(scan.partial_data) && (
                            <Tooltip title={`${partialTooltip(scan)} (click for details)`} placement="top">
                              <Chip label="Partial" size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onOpenHelp) onOpenHelp('scan-reliability');
                                }}
                                sx={{ bgcolor: 'rgba(251,191,36,0.15)', color: '#fbbf24',
                                      fontWeight: 600, fontSize: '0.62rem', height: 18,
                                      cursor: 'pointer',
                                      '&:hover': { bgcolor: 'rgba(251,191,36,0.28)' } }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', color: 'rgba(232,228,248,0.45)', py: 0.8, whiteSpace: 'nowrap' }}>
                        {scan.duration_seconds != null
                          ? Math.round(scan.duration_seconds) >= 60
                            ? `${Math.floor(Math.round(scan.duration_seconds) / 60)}m ${Math.round(scan.duration_seconds) % 60}s`
                            : `${Math.round(scan.duration_seconds)}s`
                          : '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', color: 'rgba(232,228,248,0.45)', py: 0.8 }}>
                        <Tooltip title={`Domains: ${scan.domains_list || '—'}`} placement="top">
                          <span>{scan.total_findings ?? scan.new_detections ?? '—'}</span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Box>
  );
}

export default ScanHistory;
